import { hideLoading, showLoading } from 'react-redux-loading-bar'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { errorHandler } from '../../../components/ErrorHandler'
import { getAppData } from 'featureSliced/shared/store/reducers/AppReducer/actionCreator'
import { getCategoriesData } from 'featureSliced/shared/store/reducers/CategoriesReducer/actionCreator'
import { getAllergens } from 'featureSliced/shared/store/reducers/CommonReducer/actionCreator'
import { getRestaurants } from 'featureSliced/shared/store/reducers/RestaurantsReducer/actionCreator'
import { getSpecialPrices } from 'featureSliced/shared/store/reducers/SpecialsReducer/actionCreator'
import { initTranslations } from '../store/reducers/TranslationsReducer/actionCreator'
import {
  handleLocalStorage,
  removeLocalStorageKeys,
} from '../utils/handleLocalStorage'
import { setDefaultDocumentName } from '../utils/setDefaultDocumentName'
import { useMyDispatch } from './useMyDispatch'
import enableNotifications from '../utils/webPush/initializeState'
import { v4 as uuidv4 } from 'uuid'
import { formatGuid } from '../utils/formatGuid'
import { Auth } from '@aws-amplify/auth'
import { loadTableStatus } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/actionCreator'
import { AuthSlice } from 'featureSliced/shared/store/reducers/AuthReducer'
import { checkIsLoggedIn } from 'featureSliced/shared/store/reducers/AuthReducer/actionCreator'
import { downloadCustomFont } from 'featureSliced/shared/utils/downloadCustomFont'
import { resolveCompanyFromHostname } from 'featureSliced/shared/utils/resolveCompanyFromHostname'
import { WishlistSlice } from '../store/reducers/WishlistReducer'
import { QRMBucketSlice } from '../store/reducers/QRMenuBucketReducer'
import { setPosthogTags } from '../utils/postHog/postHog'
import * as Sentry from '@sentry/react'

type StartAppType = () => void
type InitialLoadingType = () => Promise<void>
type LoadMenuDataType = () => Promise<void>

export const useStartApp = (
  setIsLoading: (value: boolean) => void
): [StartAppType, InitialLoadingType, LoadMenuDataType] => {
  //
  // Hooks
  const navigate = useNavigate()
  const dispatch = useMyDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const signOut = AuthSlice.actions.signOut
  const clearBucket = QRMBucketSlice.actions.clearBucket
  const clearWishList = WishlistSlice.actions.clearWishList

  // MAIN FUNC
  const startApp = () => {
    setDefaultDocumentName()
    dispatch(initTranslations())
    dispatch(checkIsLoggedIn())
    navigator.serviceWorker?.register('/sw.js')?.catch(errorHandler)
    if (location.pathname !== '/welcome' && location.pathname !== '/scanner') {
      initialLoading()
    }
  }

  // Functions
  const initialLoading = async () => {
    try {
      dispatch(showLoading())
      const params = saveAndRemoveSearchParams()
      const { registerId, dataEnc } = params
      let guid = params.guid
      if (!guid && !dataEnc) {
        guid = await resolveCompanyFromHostname()
      }
      if (dataEnc || guid) {
        Sentry.setTags({ company: dataEnc || guid })
      }
      loadAppData(guid, registerId, dataEnc)
    } catch (e) {
      errorHandler(e)
    }
  }

  const loadAppData = (
    guid?: string,
    registerId?: string,
    dataEnc?: string
  ) => {
    if (process.env.REACT_APP_IS_CLIC === '0') {
      if (guid || dataEnc) {
        loadQrMenu()
      } else if (
        location.pathname !== '/reservation' &&
        location.pathname !== '/booking'
      ) {
        navigate('/welcome')
      }
    }
    if (process.env.REACT_APP_IS_CLIC === '1') {
      if (guid || dataEnc) {
        loadClickCollect(registerId, dataEnc)
      } else {
        navigate('/welcome')
      }
    }
  }

  const loadQrMenu = async () => {
    try {
      await loadMenuData()
      lastStepLoading()
    } catch (e) {
      setIsLoading(false)
      errorHandler(e)
    }
  }

  const loadClickCollect = async (registerId?: string, dataEnc?: string) => {
    try {
      await dispatch(getAppData())
      if (registerId || dataEnc) {
        await loadMenuData() // loadMenuData contains getAppData
      }
      lastStepLoading()
    } catch (e) {
      setIsLoading(false)
      errorHandler(e)
    }
  }

  const lastStepLoading = () => {
    dispatch(getAllergens())
    downloadCustomFont()
    enableNotifications()
    dispatch(getRestaurants({}))
    initPosthogTags()
  }

  const initPosthogTags = () => {
    const { guid, registerId, dataEnc } = handleLocalStorage([
      'guid',
      'registerId',
      'dataEnc',
    ])
    setPosthogTags({
      company: dataEnc || guid || 'no_company',
      GUID: guid || 'no_guid',
      DATA_ENC: dataEnc || 'no_data_enc',
      REGISTER_ID: registerId || 'no_register_id',
      APP_TYPE:
        process.env.REACT_APP_IS_CLIC === '1' ? 'CLICK_COLLECT' : 'QR_MENU',
      ENVIRONMENT: process.env.REACT_APP_IS_PROD === '1' ? 'PROD' : 'DEVELOP',
    })
  }

  const loadMenuData = async () => {
    const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
    if (!guid && !dataEnc) {
      // Mirrors the guard in loadAppData; send the user to scan/enter a company.
      navigate('/welcome')
      return
    }
    try {
      await Promise.all([
        dispatch(getAppData()),
        dispatch(getCategoriesData()),
        dispatch(getSpecialPrices()),
      ])
      dispatch(hideLoading())
      if (location.pathname !== '/') {
        dispatch(loadTableStatus())
      }
    } catch (e) {
      setIsLoading(false)
      errorHandler(e)
    }
  }

  // helpers
  const saveAndRemoveSearchParams = () => {
    const sessionId = handleLocalStorage('sessionId')
    if (!sessionId) {
      handleLocalStorage({ sessionId: uuidv4() })
    }
    const guid = searchParams.get('guid')
    const dataEnc = searchParams.get('data')
    const registerId = searchParams.get('register')

    if (dataEnc) {
      const prevDataEnc = handleLocalStorage('dataEnc')
      if (dataEnc?.toLocaleLowerCase() !== prevDataEnc?.toLocaleLowerCase()) {
        handleLocalStorage({ dataEnc })
        removeLocalStorageKeys([
          'pickUpDelay',
          'pickUpTime',
          'userName',
          'guid',
          'branchId',
          'registerId',
        ])
        Auth.signOut()
        dispatch(signOut())
        dispatch(clearBucket())
        dispatch(clearWishList())
      }
      searchParams.delete('data')
      searchParams.delete('guid')
      searchParams.delete('register')
    } else {
      if (guid) {
        const prevGUID = handleLocalStorage('guid')
        const newGuid = formatGuid(guid)
        if (prevGUID !== newGuid) {
          handleLocalStorage({ guid: newGuid })
          removeLocalStorageKeys([
            'pickUpDelay',
            'pickUpTime',
            'userName',
            'dataEnc',
            'branchId',
            'registerId',
          ])
          Auth.signOut()
          dispatch(signOut())
          dispatch(clearBucket())
          dispatch(clearWishList())
        }
        searchParams.delete('guid')
      }
      if (registerId) {
        handleLocalStorage({ registerId })
        searchParams.delete('register')
      }
    }
    setSearchParams(searchParams)
    return handleLocalStorage(['guid', 'registerId', 'dataEnc'])
  }

  // RETURN
  return [startApp, initialLoading, loadMenuData]
}
